import { useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'

import { People } from './Person.jsx'
import { PosterSlot } from './WatchPanel.jsx'
import WatchScreenTabs from './WatchScreenTabs.jsx'
import {
  ALL,
  ANY,
  KINDS,
  MISCONFIGURED as ARCHIVE_MISCONFIGURED,
  RATING_THRESHOLDS,
  READY as ARCHIVE_READY,
  UNAVAILABLE as ARCHIVE_UNAVAILABLE,
  crewAverageRating,
  filterArchiveTitles,
  groupArchiveTitlesByChain,
  memberIdsInArchive,
  otherWatcherIds,
  seriesDisplayName,
  shownAtMovieNight,
  summaryLineText,
  titlesOf,
  useWatchArchive,
  yearOptions,
  yourStatusLine,
} from './watchArchive.js'
import { READY as WATCH_READY, kindLabel, seriesSeasonLabel, sourceLabel, subscriptionsOf, useWatch } from './watch.js'
import { LOADING as SESSION_LOADING, SIGNED_IN, signInHref, useSession } from '../session.js'
import { nameFor, useDirectory } from '../directory.js'
import { useDocumentTitle } from '../useDocumentTitle.js'

// The archive — screen 4 of docs/design-briefs/watch/v2/HANDOFF.md. V2-2b.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 NAMED "ARCHIVE," NEVER "EVERYTHING" — a correction from Jordan, relayed
//    mid-slice, and worth keeping because the brief itself had it wrong.
// ═══════════════════════════════════════════════════════════════════════════
//
// The brief called this screen "Everything," quoting the README's own
// section heading. The prototype (`D2D Watch v2.dc.html`) never uses that
// word as visible text anywhere: its own `<h1>` reads "Archive," the kicker
// above it reads "The archive," the nav tab's internal id is `everything`
// but its LABEL is `Archive`, and the search field's `aria-label` is
// "Search the archive." "Everything" was the brief's paraphrase of an older
// spec, not a string the design ever shows a member. Every member-visible
// string here says Archive; `everything` survives only where it was already
// the bot/site's own established vocabulary before this correction —
// `watchArchive.js`, `/api/watch/archive`, `WATCH_ARCHIVE_3A_KEY` — which is
// exactly the id the prototype's own tab agrees with once you read the
// label instead of the internal key.
//
// The route is `/watch/archive`, not `/watch/everything` — a URL is
// member-visible too, and this one matches V2-2a's own `/api/watch/archive`
// rather than inventing a second word for the identical concept.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY TITLE ANYONE HAS SEEN, LISTED OR NOT — THE ARCHIVE IS THE SOURCE,
//    `/api/watch` IS A READ-ONLY ENRICHMENT, NEVER A SECOND SOURCE OF TRUTH
// ═══════════════════════════════════════════════════════════════════════════
//
// 🔴 ROUND 2 (manager review) REVISED THIS: the first draft of this page read
// the archive only, and its own status line for a watching/stopped member
// could never show a position ("Watching · S2E4") because the archive itself
// carries none (`watchArchive.js::yourStatusLine`'s own header). For a title
// still ON the watchlist, `/api/watch`'s own summary (P6-281's `members[].
// progress`) already has it — the SAME data `WatchPanel.jsx` already loads —
// so this page now ALSO reads it, purely to enrich a listed title's status
// line. It changes nothing else: this page still never writes anything (no
// request-side code anywhere in `watchArchive.js` or here), and an unlisted
// title still reads however the archive alone describes it (`yourStatusLine`
// falls back to the bare word when no matching listed subscription is
// found — see that function's own header). `src/test/portal/watch-
// archive.test.jsx` still asserts every row comes from the archive, and adds
// the enrichment as its own, separate, non-blocking case.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 SIGNED OUT, AS THE REST OF THE PORTAL DOES — `Trips.jsx`'s OWN PATTERN,
//    NOT `WatchTitle.jsx`'s
// ═══════════════════════════════════════════════════════════════════════════
//
// `WatchTitle.jsx` has no distinct signed-out branch: a 401 from its own
// endpoint reads as the generic "unavailable" sentence today, which this
// brief did not ask this lane to fix (out of scope — see the report). This
// page is new code, so it follows `Trips.jsx`'s own, more honest shape
// instead: `useSession()` gates the fetch entirely (Trips.jsx's own words,
// "nothing is asked for until the session says there is somebody to ask
// for"), and a signed-out visitor sees a door, never a borrowed "unavailable"
// sentence that has nothing to do with their session.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 EVERY FILTER OPTION IS DERIVED FROM THE LOADED DATA — see
//    `watchArchive.js`'s own header on why the year list is never the design
//    prototype's hardcoded 2022–2026 literals (CLAUDE.md gotcha 23)
// ═══════════════════════════════════════════════════════════════════════════
//
// ═══════════════════════════════════════════════════════════════════════════
// THE SCREEN TABS — `WatchScreenTabs.jsx`, one shared component
// ═══════════════════════════════════════════════════════════════════════════
//
// The design's screen tabs are Watchlists · Archive · Leaderboards/stats ·
// Goated, and RECONCILIATION.md's rule is to show only a tab whose screen
// exists. This header used to say there was no tab bar here; V2-2b built
// `WatchScreenTabs.jsx` (rendered below), V2-5c added Leaderboards/stats, and
// Goated's tab waits for V2-8c.

export default function WatchArchive() {
  useDocumentTitle('Archive')
  const location = useLocation()
  const session = useSession()
  const signedIn = session.status === SIGNED_IN
  const archive = useWatchArchive(signedIn)
  // Round 2: the position enrichment for a LISTED title's status line (see
  // the header). Gated on `signedIn` exactly like the archive fetch itself
  // — signed out never reaches either Function. Never blocks this page's
  // own render: a loading/failed watch summary just means no enrichment
  // yet, not a reason to withhold the archive's own, primary data.
  const watch = useWatch(signedIn)
  const directory = useDirectory()

  if (session.status === SESSION_LOADING) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="archive-loading-session">
          Checking whether you are signed in.
        </p>
      </Frame>
    )
  }

  if (!signedIn) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="archive-signed-out">
          You need to be signed in to see this.{' '}
          <a href={signInHref(location)}>Sign in with Discord</a>.
        </p>
      </Frame>
    )
  }

  if (archive.state === ARCHIVE_MISCONFIGURED) {
    return (
      <Frame>
        <p className="d2d-notice" data-testid="archive-misconfigured">
          The archive is set up wrong on this deployment, so it cannot be shown until that is
          fixed — this is not about your account.
        </p>
      </Frame>
    )
  }

  if (archive.state !== ARCHIVE_READY) {
    return (
      <Frame>
        <p
          className="d2d-notice"
          data-testid={archive.state === ARCHIVE_UNAVAILABLE ? 'archive-unavailable' : 'archive-loading'}
        >
          {archive.state === ARCHIVE_UNAVAILABLE
            ? 'The archive is unavailable right now. Try again in a minute.'
            : 'Loading the archive…'}
        </p>
      </Frame>
    )
  }

  return (
    <Frame>
      <ArchiveBody
        shared={archive.shared}
        watchShared={watch.state === WATCH_READY ? watch.shared : null}
        directory={directory}
        userId={session.userId}
      />
    </Frame>
  )
}

function Frame({ children }) {
  return (
    <article className="d2d-page d2d-watch d2d-archive" data-testid="watch-archive-page">
      {
        // A later coordination note: the watch screen tabs (Watchlists ·
        // Archive) render here AND at the top of the watch panel
        // (`WatchPanel.jsx`) — one shared component, `WatchScreenTabs.jsx`.
        // It replaces the plain "← Watch" back-link this page opened with:
        // "Watchlists" is the identical destination (`/`), stated with more
        // context than a bare arrow.
      }
      <WatchScreenTabs active="archive" />
      <div className="d2d-archive__head">
        <div className="d2d-micro d2d-archive__kicker">The archive</div>
        <h1 className="d2d-archive__h1">Archive</h1>
        <p className="d2d-archive__lede">
          Every title anyone in the crew has seen — on the list or not.
        </p>
      </div>
      {children}
    </article>
  )
}

const NO_MEMBERS = []

function ArchiveBody({ shared, watchShared, directory, userId }) {
  const [kind, setKind] = useState(ALL)
  const [year, setYear] = useState(ALL)
  const [minRating, setMinRating] = useState(ANY)
  const [memberIds, setMemberIds] = useState(NO_MEMBERS)
  const [query, setQuery] = useState('')

  const titles = useMemo(() => titlesOf(shared), [shared])
  const years = useMemo(() => yearOptions(titles), [titles])
  const candidateMembers = useMemo(() => [...memberIdsInArchive(titles)], [titles])
  // Round 2: one lookup, by id, from the watchlist summary (when it has
  // loaded) — a title's own row asks this for its position enrichment
  // rather than each row re-scanning `subscriptionsOf` itself.
  const listedSubscriptionsById = useMemo(() => {
    const map = new Map()
    for (const subscription of subscriptionsOf(watchShared)) map.set(subscription.id, subscription)
    return map
  }, [watchShared])

  const filtered = useMemo(
    () => filterArchiveTitles(titles, { kind, year, minRating, memberIds, query }),
    [titles, kind, year, minRating, memberIds, query],
  )

  // Alphabetical by title — the brief's own filter list (kind, year, rating,
  // members, search) names no sort control, unlike the design prototype's
  // own `evSort` dropdown (finished date / rating / title / year / most
  // watched). Built to the brief rather than the prototype here: a sort by
  // "finished date" would need a formatted `watched_when` this slice has no
  // other consumer for (see the report), and alphabetical is the one order
  // that needs no derived state at all and is always available.
  //
  // P6-298b §2.3: `groupArchiveTitlesByChain` IS this sort now — it groups by
  // series (each series positioned by its own ROOT title) and orders each
  // series' own members in chain order, which collapses to byte-identical
  // plain alphabetical order the moment nothing shares a `series_id` (see
  // that function's own docstring).
  const sorted = useMemo(
    () => groupArchiveTitlesByChain(filtered, watchShared),
    [filtered, watchShared],
  )

  const toggleMember = (id) => {
    setMemberIds((current) => (current.includes(id) ? current.filter((x) => x !== id) : [...current, id]))
  }

  const clearFilters = () => {
    setKind(ALL)
    setYear(ALL)
    setMinRating(ANY)
    setMemberIds(NO_MEMBERS)
    setQuery('')
  }

  return (
    <div className="d2d-archive__body">
      <input
        type="search"
        aria-label="Search the archive"
        placeholder="Search every title…"
        className="d2d-archive__search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        data-testid="archive-search"
      />

      <div className="d2d-archive__filters" data-testid="archive-filters">
        <div className="d2d-archive__filtergroup">
          <span className="d2d-micro d2d-archive__filterlabel">Kind</span>
          <div className="d2d-archive__chiprow">
            <FilterChip active={kind === ALL} onClick={() => setKind(ALL)} testId="archive-kind-all">
              All
            </FilterChip>
            {KINDS.map((k) => (
              <FilterChip key={k} active={kind === k} onClick={() => setKind(k)} testId={`archive-kind-${k}`}>
                {kindLabel(k)}
              </FilterChip>
            ))}
          </div>
        </div>

        <label className="d2d-archive__filtergroup">
          <span className="d2d-micro d2d-archive__filterlabel">Year</span>
          <select
            className="d2d-archive__select"
            value={year}
            onChange={(event) => setYear(event.target.value)}
            data-testid="archive-year"
          >
            <option value={ALL}>Any year</option>
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        </label>

        <label className="d2d-archive__filtergroup">
          <span className="d2d-micro d2d-archive__filterlabel">Group rating</span>
          <select
            className="d2d-archive__select"
            value={minRating}
            onChange={(event) => setMinRating(event.target.value)}
            data-testid="archive-rating"
          >
            <option value={ANY}>Any rating</option>
            {RATING_THRESHOLDS.map((threshold) => (
              <option key={threshold} value={threshold}>
                {threshold} and up
              </option>
            ))}
          </select>
        </label>

        {candidateMembers.length > 0 ? (
          <div className="d2d-archive__filtergroup">
            <span className="d2d-micro d2d-archive__filterlabel">Who watched it</span>
            <div className="d2d-archive__chiprow">
              {candidateMembers
                .map((id) => ({ id, person: nameFor(directory, id) }))
                .sort((a, b) => a.person.label.localeCompare(b.person.label))
                .map(({ id, person }) => (
                  <FilterChip
                    key={id}
                    active={memberIds.includes(id)}
                    onClick={() => toggleMember(id)}
                    testId="archive-member-chip"
                  >
                    {person.label}
                  </FilterChip>
                ))}
            </div>
          </div>
        ) : null}

        <button
          type="button"
          className="d2d-archive__clearbtn"
          onClick={clearFilters}
          data-testid="archive-clear-filters"
        >
          Clear filters
        </button>
      </div>

      <div className="d2d-micro d2d-archive__count" data-testid="archive-count">
        {sorted.length} of {titles.length} titles
      </div>

      {sorted.length === 0 ? (
        <p className="d2d-watch__flatline" data-testid="archive-empty">
          Nothing matches. Try fewer filters.
        </p>
      ) : (
        <div className="d2d-archive__rows" data-testid="archive-rows">
          {sorted.map((title) => (
            <ArchiveTitleRow
              key={title.id}
              title={title}
              allTitles={titles}
              listedSubscription={listedSubscriptionsById.get(title.id) ?? null}
              directory={directory}
              userId={userId}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function FilterChip({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      className={`d2d-archive__chip${active ? ' d2d-archive__chip--active' : ''}`}
      onClick={onClick}
      data-testid={testId}
    >
      {children}
    </button>
  )
}

/**
 * One row. Links to the title page, matching the design's own manner (every
 * row in `everything()` is a button that opens the title — the prototype's
 * OWN internal function name still says `everything`, unrelated to what the
 * screen is called) — including for a title off the watchlist. **Round 2
 * (manager review, "no dead ends"): that link never leads to a "this title
 * doesn't exist" sentence.** `WatchTitle.jsx` now renders a read-only view
 * from this exact archive data when a title is off the watchlist but still
 * in the archive (`watchTitle.js::ARCHIVED`) — this row does not need to
 * know which case it is; the title page itself resolves it. Named
 * `ArchiveTitleRow`, not `ArchiveRow` — `WatchPanel.jsx` already has an
 * `ArchiveRow` for the finished-watchlist section, and this is a different
 * component in a different file, not a second copy of it.
 *
 * `listedSubscription` (round 2) is `/api/watch`'s own summary row for this
 * title, when one exists — `null` for a title off the watchlist. Passed
 * straight through to `yourStatusLine`, which is the only thing that reads
 * it (see that function's own header).
 *
 * `allTitles` (P6-298b) is the archive's own FULL, unfiltered title list —
 * never `sorted`/`filtered` — so `seriesDisplayName`'s own root lookup keeps
 * working even when the active filters would otherwise hide the root itself
 * (a year filter excluding Season 1 while Season 4 still matches, say).
 */
function ArchiveTitleRow({ title, allTitles, listedSubscription, directory, userId }) {
  const meta = [kindLabel(title.kind), title.year ?? '—', sourceLabel(title.kind)].join(' · ')
  const summary = summaryLineText(title)
  const others = otherWatcherIds(title, userId)
  const you = yourStatusLine(title, userId, listedSubscription)
  // P6-298b §2.3: "Tokyo Revengers · Season 2" — the series' own name (the
  // ROOT's title), prefixed only where the row's own title ISN'T that root's
  // title, with the SAME shortening `seriesSeasonLabel` (`watch.js`) already
  // gives the title page's season tabs — one label algorithm, not two.
  const seriesName = seriesDisplayName(allTitles, title)
  const displayTitle = seriesName !== null ? seriesSeasonLabel(title.title, seriesName) : title.title
  // P6-303b: a one-glyph mark, never a filter — see the report for why a
  // filter chip is not "one line" here (a new piece of filter STATE, a new
  // branch in `filterArchiveTitles`, and its own chip, not a single
  // expression like this mark).
  const atMovieNight = shownAtMovieNight(title)

  return (
    <Link className="d2d-archive__row" to={`/watch/${title.id}`} data-testid="archive-row">
      <PosterSlot subscription={title} size="archive" />
      <span className="d2d-archive__rowtext">
        <span className="d2d-archive__rowtitle">
          {atMovieNight ? (
            <span
              className="d2d-archive__movienightmark"
              data-testid="archive-row-movie-night"
              role="img"
              aria-label="Shown at movie night"
            >
              🎬{' '}
            </span>
          ) : null}
          {seriesName !== null ? (
            <span className="d2d-micro d2d-archive__rowseriesname" data-testid="archive-row-series-name">
              {seriesName}
            </span>
          ) : null}
          {seriesName !== null ? ' · ' : ''}
          {displayTitle}
        </span>
        <span className="d2d-micro d2d-archive__rowmeta">{meta}</span>
        <span className="d2d-archive__rowline">
          {summary}
          {others.length > 0 ? (
            <>
              {' — '}
              <People directory={directory} ids={others} />
            </>
          ) : null}
        </span>
        {you ? (
          <span className="d2d-archive__rowyou" data-testid="archive-row-you">
            {you}
          </span>
        ) : null}
      </span>
    </Link>
  )
}
